import { AccommodationModalDetailedProps } from "@/components/AccommodationModalDetailed/AccommodationModalDetailedProps.ts";
import { AccommodationRatePicker } from "@/components/AccommodationRatePicker/AccommodationRatePicker.tsx";
import { AmenityTag } from "@/components/AmenityTag/AmenityTag.tsx";
import Gallery from "@/components/Gallery/Gallery.tsx";
import { Close } from "@/components/Icons/utility/Close.tsx";
import { PriceAndCapacity } from "@/components/PriceAndCapacity/PriceAndCapacity.tsx";
import { useGetLowestPriceFrom } from "@/helpers/getLowestPriceFrom.ts";
import { useGTMAccommodationPreviewOpen } from "@/helpers/gtm/useGTMAccommodationPreviewOpen.ts";
import { useGetAmenityTranslated } from "@/helpers/translation/useGetAmenityTranslated.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useArrowKeyScroll } from "@/helpers/useArrowKeyScroll.ts";
import { useCloseOnEscape } from "@/helpers/useCloseOnEscape.ts";
import DOMPurify from "dompurify";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const AccommodationModalDetailed = (props: AccommodationModalDetailedProps) => {
  const { t } = useTranslation();
  const rg = useMemo(() => props.previewRG!, [props?.previewRG]);
  const { description, name } = useGetRGTranslated(rg);
  const amenities = useGetAmenityTranslated(rg?.amenities);
  const [amenitiesCollapsed, setAmenitiesCollapsed] = useState((amenities?.length ?? 0) > 8);
  const lowestPriceFrom = useGetLowestPriceFrom(rg);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useGTMAccommodationPreviewOpen(rg);
  useCloseOnEscape(props.handleCloseModal);
  useArrowKeyScroll(scrollContainerRef);

  const handleRenderDetails = () => {
    if (!description) return null;

    return (
      <div className="c-accommodation-modal__info-details">
        <h4 className="c-accommodation-modal__section-header">{t("details")}</h4>
        <p className="u-p0 c-accommodation-modal__info-description">{description}</p>
      </div>
    );
  };

  const handleRenderAmenities = () => {
    if (!amenities?.length) return null;

    return (
      <div
        className={`accModalDetailedImgFooter c-accommodation-modal__info-amenities${
          amenitiesCollapsed ? " is-collapsed" : ""
        }`}
      >
        <h4
          className="c-accommodation-modal__section-header"
          onClick={() => setAmenitiesCollapsed((v) => !v)}
        >
          {t("amenities")}
        </h4>
        <div className="c-accommodation-modal__amenity-grid">
          {amenities?.map((amenity, i) => <AmenityTag key={i} name={amenity} />)}
        </div>
      </div>
    );
  };

  const sortedImages = useMemo(() => {
    const sortedPerGalleryPositionIndex = [...(rg.images ?? [])].sort((a, b) => {
      return (a.galleryPositionIndex ?? 0) - (b.galleryPositionIndex ?? 0);
    });

    const primaryImage = sortedPerGalleryPositionIndex.find((image) => image.isPrimary);
    if (primaryImage) {
      const filteredImages = sortedPerGalleryPositionIndex.filter((image) => !image.isPrimary);
      return [primaryImage, ...filteredImages];
    }
    return sortedPerGalleryPositionIndex;
  }, [rg.images]);

  return (
    <div className={`c-accommodation-modal-wrapper${props.hideRatesPicker ? " no-rates" : ""}`}>
      <div className="c-accommodation-modal-wrapper__close">
        <button className="c-close-button" onClick={props.handleCloseModal}>
          <Close />
        </button>
      </div>
      <div className="accModalDetailed c-accommodation-modal" ref={scrollContainerRef}>
        <div className="accModalDetailedImgSection c-accommodation-modal__gallery">
          <Gallery slides={sortedImages} options={{ loop: true }} thumbnails lightbox keyboardNav />
        </div>
        <div className="accModalDetailedInfoSection c-accommodation-modal__info">
          <div className="c-accommodation-modal__info-inner">
            <h3 className="u-h4 u-fw-600 c-accommodation-modal__info-title">{name}</h3>
            <PriceAndCapacity
              priceFrom={lowestPriceFrom}
              capacity={rg.guestCapacity}
              additionalCapacity={rg.guestAdditionalCapacity}
            />
            {props.showFullDescription && (
              <div className="c-accommodation-modal__info-details">
                <div
                  className="u-p0 c-accommodation-modal__info-description u-rte"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.showFullDescription) }}
                />
              </div>
            )}
            {handleRenderDetails()}
            {handleRenderAmenities()}
          </div>
        </div>
      </div>
      {props.hideRatesPicker || (
        <div className="c-accommodation-modal-rates">
          <AccommodationRatePicker
            rateGroup={rg}
            onSelectRatePlan={props.handleCloseModal}
            showFullDescription
          />
        </div>
      )}
    </div>
  );
};
